import { NotificationService, type Notification } from '../services/predictive/NotificationService';
import { escapeHtml } from '../utils/sanitize';

const TOAST_DURATION_MS = 5000;
const MAX_VISIBLE = 5;

export class NotificationToast {
  private container: HTMLElement;
  private service: NotificationService;
  private visible: Set<string> = new Set();

  constructor(service: NotificationService) {
    this.service = service;
    this.container = document.createElement('div');
    this.container.id = 'toast-container';
    this.container.style.cssText = `
      position: fixed; top: 1rem; right: 1rem; z-index: 9999;
      display: flex; flex-direction: column; gap: 0.5rem;
      max-height: 90vh; overflow: hidden; pointer-events: none;
    `;
    document.body.appendChild(this.container);

    service.subscribe((notifications) => {
      const latest = notifications
        .filter((n) => !n.read && n.severity !== 'info')
        .slice(-MAX_VISIBLE);
      latest.forEach((n) => this.show(n));
    });
  }

  private show(notification: Notification): void {
    if (this.visible.has(notification.id)) return;
    this.visible.add(notification.id);

    const el = document.createElement('div');
    el.style.cssText = `
      pointer-events: auto;
      background: rgba(16, 22, 37, 0.95);
      backdrop-filter: blur(12px);
      border: 1px solid ${this.borderColor(notification.severity)};
      border-left: 4px solid ${this.borderColor(notification.severity)};
      border-radius: 8px;
      padding: 0.6rem 0.8rem;
      min-width: 260px;
      max-width: 380px;
      font-family: 'Outfit', sans-serif;
      font-size: 0.8rem;
      color: #f3f6fa;
      box-shadow: 0 8px 32px rgba(0,0,0,0.4);
      transform: translateX(120%);
      transition: transform 0.3s ease, opacity 0.3s ease;
      opacity: 0;
    `;

    el.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:0.25rem;">
        <strong style="color:${this.borderColor(notification.severity)};font-size:0.7rem;text-transform:uppercase;">
          ${escapeHtml(notification.severity.toUpperCase())}
        </strong>
        <button style="background:none;border:none;color:#8f9cae;cursor:pointer;padding:0;font-size:0.9rem;line-height:1;">&times;</button>
      </div>
      <div style="font-weight:500;margin-bottom:0.15rem;">${escapeHtml(notification.title)}</div>
      <div style="color:#8f9cae;font-size:0.7rem;">${escapeHtml(notification.body)}</div>
      <div style="color:#5c6b80;font-size:0.6rem;margin-top:0.2rem;">${escapeHtml(notification.timestamp.toLocaleTimeString())}</div>
    `;

    const closeBtn = el.querySelector('button')!;
    closeBtn.addEventListener('click', () => this.dismiss(el, notification.id));

    this.container.appendChild(el);

    requestAnimationFrame(() => {
      el.style.transform = 'translateX(0)';
      el.style.opacity = '1';
    });

    setTimeout(() => this.dismiss(el, notification.id), TOAST_DURATION_MS);

    this.service.markAsRead(notification.id);
  }

  private dismiss(el: HTMLElement, id: string): void {
    if (!this.visible.has(id)) return;
    el.style.transform = 'translateX(120%)';
    el.style.opacity = '0';
    setTimeout(() => {
      if (el.parentNode) el.parentNode.removeChild(el);
      this.visible.delete(id);
    }, 300);
  }

  private borderColor(severity: string): string {
    switch (severity) {
      case 'critical': return '#ff4757';
      case 'warning': return '#ffb700';
      default: return '#00d9ff';
    }
  }
}
